
import { Ball, ShapeType } from '../../types';
import { ShapeUtils } from './ShapeUtils';

export const resolveBallCollision = (b1: Ball, b2: Ball, restitution: number): number => {
  const isB1Poly = b1.shape && b1.shape !== 'CIRCLE';
  const isB2Poly = b2.shape && b2.shape !== 'CIRCLE';

  if (isB1Poly || isB2Poly) {
      if (isB1Poly && isB2Poly && b1.shape === 'SQUARE' && b2.shape === 'SQUARE' && b1.dimensions && b2.dimensions) {
          // SQUARE vs SQUARE OBB Collision (Simplified SAT)
          const w1 = b1.dimensions.x;
          const h1 = b1.dimensions.y;
          const a1 = b1.angle || 0;
          
          const w2 = b2.dimensions.x;
          const h2 = b2.dimensions.y;
          const a2 = b2.angle || 0;
          
          // For spiral boxes, they are usually aligned or slightly rotated.
          // We'll use a circle approximation for the actual impulse response 
          // but use a tighter radius to prevent the "blast" from circumscribing circles.
          // A good compromise is the average of inner and outer radius.
          const r1 = (Math.min(w1, h1) + Math.sqrt(w1*w1 + h1*h1)) / 4;
          const r2 = (Math.min(w2, h2) + Math.sqrt(w2*w2 + h2*h2)) / 4;
          
          const dx = b2.pos.x - b1.pos.x;
          const dy = b2.pos.y - b1.pos.y;
          const distanceSq = dx * dx + dy * dy;
          const minDistance = r1 + r2;
          
          if (distanceSq < minDistance * minDistance) {
              const distance = Math.sqrt(distanceSq);
              const nx = distance > 0 ? dx / distance : 1;
              const ny = distance > 0 ? dy / distance : 0;
              const overlap = minDistance - distance;
              
              const totalMass = b1.mass + b2.mass;
              const m1Ratio = b2.mass / totalMass;
              const m2Ratio = b1.mass / totalMass;
              
              b1.pos.x -= nx * overlap * m1Ratio;
              b1.pos.y -= ny * overlap * m1Ratio;
              b2.pos.x += nx * overlap * m2Ratio;
              b2.pos.y += ny * overlap * m2Ratio;
              
              const v1n = b1.vel.x * nx + b1.vel.y * ny;
              const v2n = b2.vel.x * nx + b2.vel.y * ny;
              const relVel = v1n - v2n;
              
              if (relVel > 0) {
                  const impulse = (1 + restitution) * relVel / (1/b1.mass + 1/b2.mass);
                  b1.vel.x -= (impulse / b1.mass) * nx;
                  b1.vel.y -= (impulse / b1.mass) * ny;
                  b2.vel.x += (impulse / b2.mass) * nx;
                  b2.vel.y += (impulse / b2.mass) * ny;
                  return relVel;
              }
          }
          return 0;
      }
      
      // One Poly, One Circle
          const poly = isB1Poly ? b1 : b2;
          const circle = isB1Poly ? b2 : b1;
          
          if (poly.shape === 'SQUARE' && poly.dimensions) {
              // OBB Collision (Rectangle vs Circle)
              const w = poly.dimensions.x;
              const h = poly.dimensions.y;
              const angle = poly.angle || 0;
              
              const cx = circle.pos.x;
              const cy = circle.pos.y;
              
              // Rotate circle center into OBB local space
              const cos = Math.cos(-angle);
              const sin = Math.sin(-angle);
              
              const dx = cx - poly.pos.x;
              const dy = cy - poly.pos.y;
              
              const localX = dx * cos - dy * sin;
              const localY = dx * sin + dy * cos;
              
              // Find closest point on AABB in local space
              const closestX = Math.max(-w/2, Math.min(w/2, localX));
              const closestY = Math.max(-h/2, Math.min(h/2, localY));
              
              const distX = localX - closestX;
              const distY = localY - closestY;
              const distSq = distX * distX + distY * distY;
              
              if (distSq < circle.radius * circle.radius) {
                  const dist = Math.sqrt(distSq);
                  
                  // Collision Normal in Local Space
                  let nLocalX = 0;
                  let nLocalY = 0;
                  
                  if (dist > 0) {
                      nLocalX = distX / dist;
                      nLocalY = distY / dist;
                  } else {
                      // Center is inside rectangle. Push out to nearest edge.
                      const distToRight = w/2 - localX;
                      const distToLeft = localX - (-w/2);
                      const distToTop = h/2 - localY;
                      const distToBottom = localY - (-h/2);
                      
                      const min = Math.min(distToRight, distToLeft, distToTop, distToBottom);
                      
                      if (min === distToRight) nLocalX = 1;
                      else if (min === distToLeft) nLocalX = -1;
                      else if (min === distToTop) nLocalY = 1;
                      else nLocalY = -1;
                  }
                  
                  // Rotate Normal back to World Space
                  const nWorldX = nLocalX * Math.cos(angle) - nLocalY * Math.sin(angle);
                  const nWorldY = nLocalX * Math.sin(angle) + nLocalY * Math.cos(angle);
                  
                  const overlap = circle.radius - dist; 
                  
                  let trueOverlap = overlap;
                  if (dist === 0) {
                      const distToRight = w/2 - localX;
                      const distToLeft = localX - (-w/2);
                      const distToTop = h/2 - localY;
                      const distToBottom = localY - (-h/2);
                      trueOverlap = Math.min(distToRight, distToLeft, distToTop, distToBottom) + circle.radius;
                  }

                  // Apply Physics
                  const nx = nWorldX;
                  const ny = nWorldY;
                  
                  const totalMass = b1.mass + b2.mass;
                  const m1Ratio = b2.mass / totalMass;
                  const m2Ratio = b1.mass / totalMass;
                  
                  if (isB1Poly) {
                      b1.pos.x -= nx * trueOverlap * m1Ratio;
                      b1.pos.y -= ny * trueOverlap * m1Ratio;
                      b2.pos.x += nx * trueOverlap * m2Ratio;
                      b2.pos.y += ny * trueOverlap * m2Ratio;
                  } else {
                      b1.pos.x += nx * trueOverlap * m1Ratio;
                      b1.pos.y += ny * trueOverlap * m1Ratio;
                      b2.pos.x -= nx * trueOverlap * m2Ratio;
                      b2.pos.y -= ny * trueOverlap * m2Ratio;
                  }
                  
                  const v1n = b1.vel.x * nx + b1.vel.y * ny;
                  const v2n = b2.vel.x * nx + b2.vel.y * ny;
                  
                  let relVel = 0;
                  if (isB1Poly) relVel = v2n - v1n;
                  else relVel = v1n - v2n;
                  
                  if (relVel < 0) {
                      const impulse = -(1 + restitution) * relVel / (1/b1.mass + 1/b2.mass);
                      const ix = impulse * nx;
                      const iy = impulse * ny;
                      
                      if (isB1Poly) {
                          b1.vel.x -= ix / b1.mass;
                          b1.vel.y -= iy / b1.mass;
                          b2.vel.x += ix / b2.mass;
                          b2.vel.y += iy / b2.mass;
                      } else {
                          b1.vel.x += ix / b1.mass;
                          b1.vel.y += iy / b1.mass;
                          b2.vel.x -= ix / b2.mass;
                          b2.vel.y -= iy / b2.mass;
                      }
                      return Math.abs(relVel);
                  }
                  return 0;
              }
              return 0;
          }

          const relX = circle.pos.x - poly.pos.x;
          const relY = circle.pos.y - poly.pos.y;
          const dist = Math.sqrt(relX*relX + relY*relY);
          
          const rotRad = poly.angle || 0;
          const angleToCircle = Math.atan2(relY, relX);
          const effectiveAngle = angleToCircle - rotRad;
          
          const polyRadius = ShapeUtils.getRadiusAtAngle(effectiveAngle, poly.shape!, poly.radius);
          
          if (dist < polyRadius + circle.radius) {
              // Collision detected
              const localX = relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad);
              const localY = relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad);
              
              const localNormal = ShapeUtils.getNormal({x: localX, y: localY}, poly.shape!, poly.radius);
              
              const nx = localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad);
              const ny = localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad);
              
              const overlap = (polyRadius + circle.radius) - dist;
              
              const totalMass = b1.mass + b2.mass;
              const m1Ratio = b2.mass / totalMass;
              const m2Ratio = b1.mass / totalMass;
              
              if (isB1Poly) {
                  b1.pos.x -= nx * overlap * m1Ratio;
                  b1.pos.y -= ny * overlap * m1Ratio;
                  b2.pos.x += nx * overlap * m2Ratio;
                  b2.pos.y += ny * overlap * m2Ratio;
              } else {
                  b1.pos.x += nx * overlap * m1Ratio;
                  b1.pos.y += ny * overlap * m1Ratio;
                  b2.pos.x -= nx * overlap * m2Ratio;
                  b2.pos.y -= ny * overlap * m2Ratio;
              }
              
              const v1n = b1.vel.x * nx + b1.vel.y * ny;
              const v2n = b2.vel.x * nx + b2.vel.y * ny;
              
              let relVel = 0;
              if (isB1Poly) relVel = v2n - v1n;
              else relVel = v1n - v2n;
              
              if (relVel < 0) {
                  const impulse = -(1 + restitution) * relVel / (1/b1.mass + 1/b2.mass);
                  const ix = impulse * nx;
                  const iy = impulse * ny;
                  
                  if (isB1Poly) {
                      b1.vel.x -= ix / b1.mass;
                      b1.vel.y -= iy / b1.mass;
                      b2.vel.x += ix / b2.mass;
                      b2.vel.y += iy / b2.mass;
                  } else {
                      b1.vel.x += ix / b1.mass;
                      b1.vel.y += iy / b1.mass;
                      b2.vel.x -= ix / b2.mass;
                      b2.vel.y -= iy / b2.mass;
                  }
                  return Math.abs(relVel);
              }
              return 0;
          }
      }

  const dx = b2.pos.x - b1.pos.x;
  const dy = b2.pos.y - b1.pos.y;
  const distanceSq = dx * dx + dy * dy;
  const minDistance = b1.radius + b2.radius;

  if (distanceSq < minDistance * minDistance) {
    const distance = Math.sqrt(distanceSq);
    
    let nx = 1;
    let ny = 0;
    
    if (distance > 0) {
      nx = dx / distance;
      ny = dy / distance;
    } else {
      const angle = Math.random() * Math.PI * 2;
      nx = Math.cos(angle);
      ny = Math.sin(angle);
    }

    const overlap = minDistance - distance;
    const moveX = nx * overlap / 2;
    const moveY = ny * overlap / 2;
    
    b1.pos.x -= moveX;
    b1.pos.y -= moveY;
    b2.pos.x += moveX;
    b2.pos.y += moveY;

    const v1n = b1.vel.x * nx + b1.vel.y * ny;
    const v2n = b2.vel.x * nx + b2.vel.y * ny;

    const relVel = v1n - v2n;

    if (relVel > 0) {
      const impulse = (1 + restitution) * relVel / (1 / b1.mass + 1 / b2.mass);
      
      b1.vel.x -= (impulse / b1.mass) * nx;
      b1.vel.y -= (impulse / b1.mass) * ny;
      b2.vel.x += (impulse / b2.mass) * nx;
      b2.vel.y += (impulse / b2.mass) * ny;
      
      return relVel; 
    }
  }
  return 0; 
};
